import { useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { IMAGES } from "../../lib/medskin-data";

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, x)));
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    update(clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    update(clientX);
  };
  const onUp = () => {
    dragging.current = false;
  };

  return (
    <section id="resultater" className="py-24 md:py-36 bg-[#EBE5DF]/40" data-testid="results-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C4A981]"></span>
              <span className="text-[11px] uppercase tracking-[0.3em] text-[#635D56]">Før / Etter</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.08] text-[#2C2C2A] tracking-tight font-light" data-testid="results-headline">
              Resultater som <em className="italic text-[#4A4238]">snakker for seg selv</em>.
            </h2>
          </div>
          <p className="lg:col-span-5 text-base md:text-lg text-[#635D56] font-light leading-relaxed">
            Dra i håndtaket for å se transformasjonen. Hver behandlingsplan er individuell – resultater varierer.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[2rem] overflow-hidden border border-[#E5E0DA] select-none"
          ref={containerRef}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
          data-testid="before-after-slider"
        >
          {/* Etter (base) */}
          <div className="aspect-[16/9] md:aspect-[21/9] w-full bg-[#EBE5DF]">
            <img
              src={IMAGES.beforeAfter}
              alt="Etter behandling"
              className="w-full h-full object-cover"
              draggable={false}
            />
          </div>

          {/* Før (overlay) */}
          <div
            className="absolute top-0 left-0 h-full overflow-hidden"
            style={{ width: `${pos}%` }}
          >
            <img
              src={IMAGES.hero}
              alt="Før behandling"
              className="w-full h-full object-cover"
              style={{ width: containerRef.current ? `${containerRef.current.offsetWidth}px` : "100%", maxWidth: "none" }}
              draggable={false}
            />
            <div className="absolute inset-0 bg-[#2C2C2A]/10"></div>
          </div>

          {/* Labels */}
          <div className="absolute top-5 left-5 bg-[#F7F6F3]/85 backdrop-blur-md rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-[#2C2C2A]">
            Før
          </div>
          <div className="absolute top-5 right-5 bg-[#4A4238] text-[#F7F6F3] rounded-full px-4 py-1.5 text-[10px] uppercase tracking-[0.25em]">
            Etter
          </div>

          {/* Handle */}
          <div
            className="absolute top-0 bottom-0 w-px bg-[#F7F6F3]"
            style={{ left: `${pos}%`, transform: "translateX(-0.5px)" }}
          >
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-[#F7F6F3] border border-[#D1C9C0] shadow-[0_10px_30px_-10px_rgba(74,66,56,0.4)] flex items-center justify-center cursor-ew-resize">
              <span className="text-[#4A4238] text-lg leading-none tracking-tighter">‹›</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
